<template>
  <div style="padding: 24px">
    <a-card
      :bordered="false"
      title="角色权限"
    >
      <div style="margin-bottom: 16px">
        <a-button
          type="primary"
          @click="openCreate"
        >新增角色</a-button>
      </div>

      <a-table
        :columns="columns"
        :data-source="list"
        :loading="loading"
        row-key="id"
        :pagination="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'menuPermissions'">
            <a-tag
              v-for="p in visibleMenuPermissions(record.menuPermissions)"
              :key="p"
              style="margin-bottom: 2px"
            >{{ menuLabelMap[p] || p }}</a-tag>
          </template>
          <template v-if="column.dataIndex === 'buttonPermissions'">
            <a-tag
              v-for="b in record.buttonPermissions"
              :key="b"
              color="blue"
              style="margin-bottom: 2px"
            >{{ btnLabelMap[b] || b }}</a-tag>
          </template>
          <template v-if="column.dataIndex === 'dimensionIds'">
            <span v-if="!record.dimensionIds || record.dimensionIds.length === 0">全部</span>
            <a-tag
              v-else
              v-for="d in record.dimensionIds"
              :key="d"
            >{{ dimLabelMap[d] || d }}</a-tag>
          </template>
          <template v-if="column.dataIndex === 'isEnabled'">
            <a-tag :color="record.isEnabled ? 'green' : 'red'">{{ record.isEnabled ? '启用' : '禁用' }}</a-tag>
          </template>
          <template v-if="column.dataIndex === 'action'">
            <a-button
              type="link"
              size="small"
              @click="openEdit(record)"
            >编辑</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 新增/编辑弹窗 -->
    <a-modal
      v-model:open="modalVisible"
      :title="editingRole ? '编辑角色' : '新增角色'"
      :confirm-loading="saving"
      width="640px"
      @ok="handleSave"
    >
      <a-form :label-col="{ span: 5 }">
        <a-form-item label="角色名称">
          <a-input
            v-model:value="form.name"
            placeholder="如：管理员"
          />
        </a-form-item>
        <a-form-item label="角色编码">
          <a-input
            v-model:value="form.code"
            :disabled="!!editingRole"
            placeholder="如：admin"
          />
        </a-form-item>
        <a-form-item label="菜单权限">
          <div class="menu-perm-groups">
            <!-- 独立 -->
            <div class="menu-group">
              <a-checkbox
                value="/dashboard"
                :checked="form.menuPermissions.includes('/dashboard')"
                @change="toggleMenu('/dashboard', $event.target.checked)"
              >控制台</a-checkbox>
            </div>
            <!-- 订单统计 -->
            <div class="menu-group">
              <div class="menu-group-header">
                <a-checkbox
                  :checked="isGroupAllChecked(orderMenus)"
                  :indeterminate="isGroupIndeterminate(orderMenus)"
                  @change="toggleGroup(orderMenus, $event.target.checked)"
                >订单统计</a-checkbox>
              </div>
              <div class="menu-group-items">
                <a-checkbox
                  v-for="item in orderMenus"
                  :key="item.value"
                  :value="item.value"
                  :checked="form.menuPermissions.includes(item.value)"
                  @change="toggleMenu(item.value, $event.target.checked)"
                >{{ item.label }}</a-checkbox>
              </div>
            </div>
            <!-- 订单记录 -->
            <div class="menu-group">
              <div class="menu-group-header">
                <a-checkbox
                  :checked="isGroupAllChecked(recordMenus)"
                  :indeterminate="isGroupIndeterminate(recordMenus)"
                  @change="toggleGroup(recordMenus, $event.target.checked)"
                >订单记录</a-checkbox>
              </div>
              <div class="menu-group-items">
                <a-checkbox
                  v-for="item in recordMenus"
                  :key="item.value"
                  :value="item.value"
                  :checked="form.menuPermissions.includes(item.value)"
                  @change="toggleMenu(item.value, $event.target.checked)"
                >{{ item.label }}</a-checkbox>
              </div>
            </div>
            <!-- 系统管理 -->
            <div class="menu-group">
              <div class="menu-group-header">
                <a-checkbox
                  :checked="isGroupAllChecked(systemMenus)"
                  :indeterminate="isGroupIndeterminate(systemMenus)"
                  @change="toggleGroup(systemMenus, $event.target.checked)"
                >系统管理</a-checkbox>
              </div>
              <div class="menu-group-items">
                <a-checkbox
                  v-for="item in systemMenus"
                  :key="item.value"
                  :value="item.value"
                  :checked="form.menuPermissions.includes(item.value)"
                  @change="toggleMenu(item.value, $event.target.checked)"
                >{{ item.label }}</a-checkbox>
              </div>
            </div>
            <!-- 运营工具 -->
            <div class="menu-group">
              <div class="menu-group-header">
                <a-checkbox
                  :checked="isGroupAllChecked(operateMenus)"
                  :indeterminate="isGroupIndeterminate(operateMenus)"
                  @change="toggleGroup(operateMenus, $event.target.checked)"
                >运营工具</a-checkbox>
              </div>
              <div class="menu-group-items">
                <a-checkbox
                  v-for="item in operateMenus"
                  :key="item.value"
                  :value="item.value"
                  :checked="form.menuPermissions.includes(item.value)"
                  @change="toggleMenu(item.value, $event.target.checked)"
                >{{ item.label }}</a-checkbox>
              </div>
            </div>
            <!-- 文档 -->
            <div class="menu-group">
              <div class="menu-group-header">
                <a-checkbox
                  :checked="isGroupAllChecked(docsMenus)"
                  :indeterminate="isGroupIndeterminate(docsMenus)"
                  @change="toggleGroup(docsMenus, $event.target.checked)"
                >文档</a-checkbox>
              </div>
              <div class="menu-group-items">
                <a-checkbox
                  v-for="item in docsMenus"
                  :key="item.value"
                  :value="item.value"
                  :checked="form.menuPermissions.includes(item.value)"
                  @change="toggleMenu(item.value, $event.target.checked)"
                >{{ item.label }}</a-checkbox>
              </div>
            </div>
          </div>
        </a-form-item>
        <a-form-item label="按钮权限">
          <a-checkbox-group
            v-model:value="form.buttonPermissions"
            :options="btnAllOptions"
          />
        </a-form-item>
        <a-form-item label="维度权限">
          <a-select
            v-model:value="form.dimensionIds"
            mode="multiple"
            :options="dimOptions"
            placeholder="留空表示全部可见"
            allow-clear
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="状态">
          <a-switch
            v-model:checked="form.isEnabled"
            checked-children="启用"
            un-checked-children="禁用"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { message } from "ant-design-vue";
import { get, postJson } from "@/utils/request";
import { analyticsUrl } from "@/utils/api";

/* ---------- 常量映射 ---------- */
const menuLabelMap = {
  "/dashboard": "控制台",
  "/order/daily": "全收款",
  "/order/app-daily": "日收款",
  "/order/hourly-payment": "小时统计",
  "/order/link-stats": "链接统计",
  "/order/customer-service-stats": "客服统计",
  "/order/hlb-service-transfer-stats": "合利宝转接",
  "/order/app-monthly": "月收款",
  "/order/ltv": "LTV报告",
  "/order/ltv-v2": "LTV-维度",
  "/order/pay-refund-daily": "收退款-财务",
  "/order/daily-stat": "每日订单",
  "/order/deduction-tag-daily": "预扣款统计",
  "/order/deduction-forecast-v2": "预扣款统计V2",
  "/order/pay-orders": "下单记录",
  "/order/payment-records": "支付记录",
  "/order/airwallex": "空中云汇",
  "/order/hlb": "合利宝",
  "/order/kq": "快钱",
  "/system/user": "用户管理",
  "/system/role": "角色权限",
  "/operate/user-query": "客服查询",
  "/operate/external-agreement-conf": "外部配置",
  "/operate/ocean-engine-conversion": "巨量回传",
  "/operate/fusion-payment-config": "融合支付配置",
  "/operate/ad-platform-app": "投放媒体",
  "/docs/flow-events": "Flow打点",
};
const visibleMenuPermissions = (permissions) => (permissions || []).filter((path) => path !== "/ad/rules");

const orderMenus = [
  { label: "全收款", value: "/order/daily" },
  { label: "日收款", value: "/order/app-daily" },
  { label: "小时统计", value: "/order/hourly-payment" },
  { label: "链接统计", value: "/order/link-stats" },
  { label: "客服统计", value: "/order/customer-service-stats" },
  { label: "合利宝转接", value: "/order/hlb-service-transfer-stats" },
  { label: "月收款", value: "/order/app-monthly" },
  { label: "LTV-应用", value: "/order/ltv" },
  { label: "LTV-维度", value: "/order/ltv-v2" },
  { label: "收退款-财务", value: "/order/pay-refund-daily" },
  { label: "每日订单", value: "/order/daily-stat" },
  { label: "预扣款统计", value: "/order/deduction-tag-daily" },
  { label: "预扣款统计V2", value: "/order/deduction-forecast-v2" },
];
const recordMenus = [
  { label: "下单记录", value: "/order/pay-orders" },
  { label: "支付记录", value: "/order/payment-records" },
  { label: "空中云汇", value: "/order/airwallex" },
  { label: "合利宝", value: "/order/hlb" },
  { label: "快钱", value: "/order/kq" },
];
const systemMenus = [
  { label: "用户管理", value: "/system/user" },
  { label: "角色权限", value: "/system/role" },
];
const operateMenus = [
  { label: "客服查询", value: "/operate/user-query" },
  { label: "外部配置", value: "/operate/external-agreement-conf" },
  { label: "巨量回传", value: "/operate/ocean-engine-conversion" },
  { label: "融合支付配置", value: "/operate/fusion-payment-config" },
  { label: "投放媒体", value: "/operate/ad-platform-app" },
];
const docsMenus = [{ label: "Flow打点", value: "/docs/flow-events" }];

const isGroupAllChecked = (items) =>
  items.every((i) => form.menuPermissions.includes(i.value));
const isGroupIndeterminate = (items) =>
  items.some((i) => form.menuPermissions.includes(i.value)) &&
  !items.every((i) => form.menuPermissions.includes(i.value));

const toggleMenu = (value, checked) => {
  if (checked) {
    if (!form.menuPermissions.includes(value)) form.menuPermissions.push(value);
  } else {
    form.menuPermissions = form.menuPermissions.filter((v) => v !== value);
  }
};
const toggleGroup = (items, checked) => {
  if (checked) {
    items.forEach((i) => {
      if (!form.menuPermissions.includes(i.value))
        form.menuPermissions.push(i.value);
    });
  } else {
    const vals = items.map((i) => i.value);
    form.menuPermissions = form.menuPermissions.filter(
      (v) => !vals.includes(v)
    );
  }
};

const btnLabelMap = {
  "ltv-v2:upload": "上传数据",
  "ltv-v2:reset": "重置上传字段",
  "ltv-v2:download": "下载/导出",
  "ltv-v2:dimension": "维度管理",
  "ltv-v2:retention-save": "保存留存率",
};

const btnAllOptions = Object.entries(btnLabelMap).map(([v, l]) => ({
  label: l,
  value: v,
}));

/* ---------- 维度选项 ---------- */
const dimOptions = ref([]);
const dimLabelMap = reactive({});

const fetchDimensions = async () => {
  try {
    const json = await get(analyticsUrl("/order/stats/cohort/dimension/list"));
    const arr = json.data?.data;
    if (Array.isArray(arr)) {
      dimOptions.value = arr
        .filter((d) => d.isEnabled)
        .map((d) => ({ label: d.name, value: d.id }));
      arr.forEach((d) => {
        dimLabelMap[d.id] = d.name;
      });
    }
  } catch {
    /* ignore */
  }
};

/* ---------- 列表 ---------- */
const loading = ref(false);
const list = ref([]);

const columns = [
  { title: "ID", dataIndex: "id", width: 60 },
  { title: "名称", dataIndex: "name", width: 120 },
  { title: "编码", dataIndex: "code", width: 100 },
  { title: "菜单权限", dataIndex: "menuPermissions" },
  { title: "按钮权限", dataIndex: "buttonPermissions", width: 200 },
  { title: "维度权限", dataIndex: "dimensionIds", width: 160 },
  { title: "状态", dataIndex: "isEnabled", width: 80 },
  { title: "操作", dataIndex: "action", width: 80 },
];

const fetchList = async () => {
  loading.value = true;
  try {
    const json = await get("/v1/analytics/system/role/list");
    if (json.code === 200 && Array.isArray(json.data)) {
      list.value = json.data;
    }
  } finally {
    loading.value = false;
  }
};

/* ---------- 新增/编辑 ---------- */
const modalVisible = ref(false);
const saving = ref(false);
const editingRole = ref(null);
const form = reactive({
  name: "",
  code: "",
  menuPermissions: [],
  buttonPermissions: [],
  dimensionIds: [],
  isEnabled: true,
});

const resetForm = () => {
  form.name = "";
  form.code = "";
  form.menuPermissions = [];
  form.buttonPermissions = [];
  form.dimensionIds = [];
  form.isEnabled = true;
};

const openCreate = () => {
  editingRole.value = null;
  resetForm();
  modalVisible.value = true;
};

const openEdit = (record) => {
  editingRole.value = record;
  form.name = record.name;
  form.code = record.code;
  form.menuPermissions = visibleMenuPermissions(record.menuPermissions);
  form.buttonPermissions = [...(record.buttonPermissions || [])];
  form.dimensionIds = [...(record.dimensionIds || [])];
  form.isEnabled = record.isEnabled;
  modalVisible.value = true;
};

const handleSave = async () => {
  if (!form.name || !form.code) {
    message.warning("请填写角色名称和编码");
    return;
  }
  saving.value = true;
  try {
    const url = editingRole.value
      ? "/v1/analytics/system/role/update"
      : "/v1/analytics/system/role/create";
    const params = editingRole.value
      ? {
          id: editingRole.value.id,
          name: form.name,
          menuPermissions: form.menuPermissions,
          buttonPermissions: form.buttonPermissions,
          dimensionIds: form.dimensionIds,
          isEnabled: form.isEnabled,
        }
      : {
          name: form.name,
          code: form.code,
          menuPermissions: form.menuPermissions,
          buttonPermissions: form.buttonPermissions,
          dimensionIds: form.dimensionIds,
          isEnabled: form.isEnabled,
        };
    const json = await postJson(url, params);
    if (json.code === 200) {
      message.success(editingRole.value ? "更新成功" : "创建成功");
      modalVisible.value = false;
      fetchList();
    } else {
      message.error(json.content || "操作失败");
    }
  } finally {
    saving.value = false;
  }
};

/* ---------- 初始化 ---------- */
onMounted(() => {
  fetchList();
  fetchDimensions();
});
</script>

<style scoped>
.menu-perm-groups {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.menu-group {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fafafa;
}
.menu-group-header {
  margin-bottom: 8px;
  font-weight: 500;
}
.menu-group-items {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  padding-left: 4px;
}
</style>
